<template>
  <section
    class="dealer-team"
    :class="{ 'dealer-team--no-members': !teamMembers.length }">
    <div class="dealer-team__container">
      <!-- TextParagraph Section -->
      <div class="dealer-team__text">
        <text-paragraph
          :pre-title="preTitle"
          :title="title"
          :description="description"
          :show-ctas="showCtas"
          :primary-cta-text="primaryCtaText"
          :secondary-cta-text="secondaryCtaText"
          @primary-cta-click="$emit('primary-cta-click')"
          @secondary-cta-click="$emit('secondary-cta-click')" />
      </div>

      <!-- Team Cards Section -->
      <div
        v-if="teamMembers.length"
        ref="cardsRef"
        class="dealer-team__cards">
        <card-team
          v-for="(member, index) in teamMembers"
          :key="index"
          :image="member.image"
          :image-alt="member.imageAlt"
          :name="member.name"
          :role="member.role" />
      </div>
    </div>
  </section>
</template>

<script setup>
  import TextParagraph from '../TextParagraph.vue'
  import CardTeam from './CardTeam.vue'

  // Props
  const props = defineProps({
    preTitle: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: false,
      default: ''
    },
    showCtas: {
      type: Boolean,
      default: false
    },
    primaryCtaText: {
      type: String,
      default() {
        return useI18n().t('dealer.defaultTexts.discoverMore')
      }
    },
    secondaryCtaText: {
      type: String,
      default() {
        return useI18n().t('dealer.defaultTexts.contactUs')
      }
    },
    teamMembers: {
      type: Array,
      required: true
    }
  })

  // Emits
  defineEmits([ 'primary-cta-click', 'secondary-cta-click' ])

  // --- Allineamento card: uniforma l'altezza della riga "nome" tra tutte le
  // card (come la normalizzazione delle StoreCard) così la barra divisoria
  // cade sempre allo stesso punto, anche con nomi lunghi su più righe.
  const cardsRef = ref(null)

  const normalizeNameHeights = async () => {
    const container = cardsRef.value
    if (!container) return

    // reset per misurare le altezze naturali
    container.style.setProperty('--card-name-min-height', 'auto')
    await nextTick()

    let max = 0
    container.querySelectorAll('.card-team__name').forEach(el => {
      const h = el.getBoundingClientRect().height
      if (h > max) max = h
    })

    if (max > 0) {
      container.style.setProperty('--card-name-min-height', `${Math.ceil(max)}px`)
    }
  }

  const debounce = (fn, delay) => {
    let t
    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn(...args), delay)
    }
  }
  const debouncedNormalize = debounce(normalizeNameHeights, 150)

  onMounted(() => {
    normalizeNameHeights()
    // ricalcola quando i font sono pronti (la misura cambia col font reale)
    if (import.meta.client && document?.fonts?.ready) {
      document.fonts.ready.then(normalizeNameHeights)
    }
    window.addEventListener('resize', debouncedNormalize)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', debouncedNormalize)
  })

  watch(() => props.teamMembers, () => {
    nextTick(normalizeNameHeights)
  })
</script>

<style lang="scss" scoped>
.dealer-team {
  width: 100%;
  background-color: var(--color-light-grey); // Gray background

  &__container {
    max-width: var(--grid-max-width);
    margin: 0 auto;
  }

  &__text {
    display: flex;
    justify-content: center;
    padding: var(--space-kilo) 0 0;

    @include mq(medium) {
      padding: var(--space-kilo) var(--space-kilo) 32px;
    }

    // Text container centered with 60vw width, max 650px
    :deep(.text-paragraph__container) {
      text-align: left;

      @include mq(medium) {
        width: 60vw;
        max-width: 650px;
        padding: 0;
        text-align: left;
      }
    }
  }

  &__cards {
    @include container;

    gap: 24px;
    padding-bottom: var(--space-kilo);

    @include page-x-padding;

    @include mq(medium) {
      padding: 0 var(--space-kilo) var(--space-kilo);
      justify-content: flex-start;
    }

    :deep(.card-team) {
      grid-column: span 6;

      @include mq(tablet) {
        grid-column: span 4;
      }

      @include mq(medium) {
        grid-column: span 3;
      }

      @include mq(large) {
        grid-column: span 2;
      }
    }
  }

  // Stato senza membri: manca la griglia card che dava respiro sotto, quindi
  // la sezione si chiudeva di colpo. Ripristina solo un padding inferiore
  // simmetrico — allineamento del testo invariato.
  &--no-members {
    .dealer-team__text {
      padding-bottom: var(--space-kilo);
    }
  }
}
</style>
